'use client';

// Third Party
import * as React from 'react';
import { useSession } from '@lib/auth/client';

// Utils
import { useQuery } from '@apollo/client/react';
import {
  UserReferralCampaignsForCurrentUserDocument,
  UserReferralCampaignState,
} from '@gql';

export type DrawerState = 'open' | 'closed';

export type ReferEarnDrawerContextValue = {
  drawerState: DrawerState;
  isRegisteredInCampaign: boolean;
  isLoadingCampaign: boolean;
  activeCampaign: {
    code: string;
    rewardsBalanceCents: number;
    rewardsIssuedCount: number;
    rewardsRemaining: number | null;
  } | null;
  openDrawer: VoidFunction;
  closeDrawer: VoidFunction;
};

interface Props {
  children: React.ReactNode;
}

export const ReferEarnDrawerContext = React.createContext<
  ReferEarnDrawerContextValue | undefined
>(undefined);

export const ReferEarnDrawerProvider: React.FC<Props> = ({ children }) => {
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';
  const [drawerState, setDrawerState] = React.useState<DrawerState>('closed');

  const { data, loading } = useQuery(
    UserReferralCampaignsForCurrentUserDocument,
    {
      variables: { includeInactive: false },
      skip: !isAuthenticated,
    }
  );

  // Check if user has an active referral campaign
  const activeCampaign = React.useMemo(() => {
    if (
      data?.userReferralCampaignsForCurrentUser?.__typename ===
      'UserReferralCampaignCollection'
    ) {
      const campaigns = data.userReferralCampaignsForCurrentUser.entries;
      const active = campaigns.find(
        (c: { state: UserReferralCampaignState }) =>
          c.state === UserReferralCampaignState.Active
      );
      if (active) {
        return {
          code: active.code,
          rewardsBalanceCents: active.rewardsBalanceCents,
          rewardsIssuedCount: active.rewardsIssuedCount,
          rewardsRemaining: active.rewardsRemaining ?? null,
        };
      }
    }
    return null;
  }, [data]);

  const isRegisteredInCampaign = activeCampaign !== null;

  const openDrawer = React.useCallback(() => {
    setDrawerState('open');
  }, []);

  const closeDrawer = React.useCallback(() => {
    setDrawerState('closed');
  }, []);

  const contextValue: ReferEarnDrawerContextValue = React.useMemo(
    () => ({
      drawerState,
      isRegisteredInCampaign,
      isLoadingCampaign: loading,
      activeCampaign,
      openDrawer,
      closeDrawer,
    }),
    [
      drawerState,
      isRegisteredInCampaign,
      loading,
      activeCampaign,
      openDrawer,
      closeDrawer,
    ]
  );

  return (
    <ReferEarnDrawerContext.Provider value={contextValue}>
      {children}
    </ReferEarnDrawerContext.Provider>
  );
};

export const useReferEarnDrawer = () => {
  const context = React.useContext(ReferEarnDrawerContext);

  if (context === undefined) {
    throw new Error(
      'useReferEarnDrawer must be used within a ReferEarnDrawerProvider'
    );
  }

  return context;
};
